import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function AwardsSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'grid';
    const columns = config.columns || 3;
    const items = content.items || [];

    if (items.length === 0) return null;

    const getItemImage = (item) => {
        if (item.image_folder_id && item.image_file_id && mediaFolders[item.image_folder_id]) {
            const found = mediaFolders[item.image_folder_id].find(f => String(f.id) === String(item.image_file_id));
            if (found) return found.image_url;
        }
        return item.image_url || null;
    };

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const gridCls = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    const TrophyIcon = () => (
        <svg className="w-12 h-12" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3h14M5 3v4a7 7 0 007 7m-7-11H2m17 0h3M5 7H3m16 0h2M12 14v4m-4 4h8m-4-4c3.866 0 7-3.134 7-7V3H5v4c0 3.866 3.134 7 7 7z" />
        </svg>
    );

    const AwardCard = ({ item, i, large = false }) => {
        const img = getItemImage(item);
        const Wrapper = item.link ? 'a' : 'div';
        const wrapperProps = item.link ? resolveLinkProps(item.link) : {};
        return (
            <Wrapper {...wrapperProps}
                className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${item.link ? 'cursor-pointer' : ''}`}
                style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms' }}>
                <div className={`${large ? 'p-8 md:p-10' : 'p-6'} text-center`}>
                    {/* Image or trophy icon */}
                    <div className={`mx-auto ${large ? 'w-24 h-24' : 'w-16 h-16'} rounded-full flex items-center justify-center overflow-hidden border-2 transition-transform duration-300 group-hover:scale-110`}
                        style={{ borderColor: hexToRgba(s.accent, 0.2), backgroundColor: hexToRgba(s.accent, 0.05) }}>
                        {img ? (
                            <img src={img} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                            <TrophyIcon />
                        )}
                    </div>

                    {/* Year badge */}
                    {item.year && (
                        <span className={`inline-block ${large ? 'text-xs px-3 py-1 mt-5' : 'text-[10px] px-2.5 py-0.5 mt-4'} font-bold rounded-full text-white`}
                            style={{ backgroundColor: s.accent }}>
                            {item.year}
                        </span>
                    )}

                    <h4 className={`font-bold ${large ? 'text-xl mt-4' : 'text-base mt-3'} leading-snug`} style={{ color: s.text }}>{item.title}</h4>

                    {item.issuer && (
                        <span className={`block ${large ? 'text-sm mt-2' : 'text-xs mt-1.5'} font-medium`} style={{ color: s.secondary }}>{item.issuer}</span>
                    )}

                    {item.description && (
                        <p className={`${large ? 'text-sm mt-3' : 'text-xs mt-2'} leading-relaxed ${large ? '' : 'line-clamp-3'}`} style={{ color: s.textSub }}>{item.description}</p>
                    )}
                </div>
            </Wrapper>
        );
    };

    // ── Timeline variant ──
    if (variant === 'timeline') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Reconocimientos'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="relative max-w-4xl mx-auto">
                        {/* Center line (desktop) / left line (mobile) */}
                        <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 md:-translate-x-1/2" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb' }} />
                        <div className="space-y-12">
                            {items.map((item, i) => {
                                const img = getItemImage(item);
                                const isLeft = i % 2 === 0;
                                const Wrapper = item.link ? 'a' : 'div';
                                const wrapperProps = item.link ? resolveLinkProps(item.link) : {};
                                return (
                                    <div key={i} className={`relative flex items-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                        style={{ transitionDelay: visible ? `${200 + i * 150}ms` : '0ms' }}>
                                        {/* Card */}
                                        <div className={`w-full pl-16 md:pl-0 md:w-5/12 ${isLeft ? 'md:text-right md:pr-10' : 'md:ml-auto md:pl-10'}`}>
                                            <Wrapper {...wrapperProps} className={`${card} p-5 block group transition-all hover:-translate-y-0.5 hover:shadow-lg ${item.link ? 'cursor-pointer' : ''}`}>
                                                <div className={`flex items-center gap-3 ${isLeft ? 'md:flex-row-reverse' : ''}`}>
                                                    <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden shrink-0 border-2 transition-transform duration-300 group-hover:scale-110"
                                                        style={{ borderColor: hexToRgba(s.accent, 0.2), backgroundColor: hexToRgba(s.accent, 0.05) }}>
                                                        {img ? (
                                                            <img src={img} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                                                        ) : (
                                                            <svg className="w-6 h-6" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3h14M5 3v4a7 7 0 007 7m-7-11H2m17 0h3M5 7H3m16 0h2M12 14v4m-4 4h8m-4-4c3.866 0 7-3.134 7-7V3H5v4c0 3.866 3.134 7 7 7z" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        {item.year && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: s.accent }}>{item.year}</span>}
                                                        <h4 className="font-bold text-base mt-1" style={{ color: s.text }}>{item.title}</h4>
                                                    </div>
                                                </div>
                                                {item.issuer && <span className="block text-xs font-medium mt-2" style={{ color: s.secondary }}>{item.issuer}</span>}
                                                {item.description && <p className="text-xs mt-1.5 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                            </Wrapper>
                                        </div>
                                        {/* Center dot */}
                                        <div className="absolute left-3.5 md:left-1/2 md:-translate-x-1/2 w-5 h-5 rounded-full border-4 z-10"
                                            style={{ backgroundColor: s.accent, borderColor: s.dark ? 'rgba(0,0,0,0.3)' : '#ffffff' }} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Featured variant ──
    if (variant === 'featured' && items.length > 0) {
        const [featured, ...rest] = items;
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Reconocimientos'} subtitle={content.subtitle} config={config} s={s} />

                    {/* Featured award */}
                    <div className="max-w-2xl mx-auto mb-10">
                        <AwardCard item={featured} i={0} large />
                    </div>

                    {/* Remaining awards */}
                    {rest.length > 0 && (
                        <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                            {rest.map((item, i) => (
                                <AwardCard key={i} item={item} i={i + 1} />
                            ))}
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grid (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Reconocimientos'} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                    {items.map((item, i) => (
                        <AwardCard key={i} item={item} i={i} />
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
